import { Box, Chip as MuiChip, styled, SxProps, Theme } from '@mui/material';
import React, { FC, useMemo } from 'react';

type Props = {
  text: string;
  fontSize?: string;
  color?: string;
  variant?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};
type ChipWrapperProps = Omit<Props, 'text'>;

const ChipWrapper = styled(Box)<ChipWrapperProps>(({ theme, disabled, color }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  height: 'max-content',
  width: 'max-content',
  fontSize: 12,
  padding: '1px 8px',
  borderRadius: '2px',
  lineHeight: '130%',
  color: disabled ? '#bbbab7' : color,
  fontFamily: theme.typography.fontFamily,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '&:hover': {
    cursor: disabled ? 'not-allowed' : 'pointer',
  },
}));

const defaultColors = {
  'off track': '#E04732',
  'on track': '#288E49',
};

const FilledChipWrapper = styled(MuiChip)(({ theme }) => ({
  height: 'unset',
  fontSize: '11px',
  borderRadius: '4px',
  backgroundColor: 'lightblue',
  color: '#456BD9',
  fontFamily: theme.typography.fontFamily,
  ' .MuiChip-label': {
    paddingLeft: '5px',
    paddingRight: '5px',
  },
}));

const Chip: FC<Props> = ({ text, fontSize, color, variant = 'outlined', disabled, sx }) => {
  const chipColor = useMemo(
    () => color || defaultColors[text?.toLowerCase() as keyof typeof defaultColors],
    [color, text]
  );

  if (variant === 'filled') return <FilledChipWrapper label={text} disabled={disabled} sx={sx} />;

  return (
    <ChipWrapper fontSize={fontSize} color={chipColor} borderColor={chipColor} disabled={disabled} sx={sx}>
      {text}
    </ChipWrapper>
  );
};

export default Chip;
